import { render } from "preact";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import type {
  ComputerDifficulty,
  DifficultySelection,
  GameMode,
  GameState,
  PlayerId,
  SinglePlayerMode,
} from "@tanks/game-core";
import { INPUT_BITS, type ServerMessage } from "@tanks/protocol";
import { BattlefieldRenderer } from "./renderer";
import {
  GameSocket,
  movementAxes,
  movementMask,
  type ConnectionStatus,
} from "./network";
import "./styles.css";

const TOKEN_PATTERN = /^[a-f0-9]{32}$/;
type CreateGameMode = Exclude<GameMode, "single-player">;
const CONTROL_LAYOUT_STORAGE_KEY = "tanks-control-layout";
const RULES_PREFERENCE_STORAGE_KEY = "tanks-rules-preferences";
const RULES_SESSION_PREFIX = "tanks-rules-seen:";
const COMPUTER_PREFERENCE_STORAGE_KEY = "tanks-computer-preference";

function readControlsSwapped(): boolean {
  try {
    return window.localStorage.getItem(CONTROL_LAYOUT_STORAGE_KEY) === "swapped";
  } catch {
    return false;
  }
}

function gameModeLabel(
  mode: GameMode,
  singlePlayerMode: SinglePlayerMode | null = null,
): string {
  if (mode === "capture-the-flag") return "CAPTURE THE FLAG";
  if (mode === "single-player") {
    return singlePlayerMode === "capture-the-flag"
      ? "SINGLE PLAYER · CAPTURE THE FLAG"
      : "SINGLE PLAYER";
  }
  return "LAST TANK STANDING";
}

function usesCaptureTheFlag(state: GameState | null): boolean {
  return state?.flags !== null && state?.flags !== undefined;
}

function difficultyLabel(value: DifficultySelection | ComputerDifficulty): string {
  return value.toUpperCase();
}

interface ComputerPreference {
  selection: DifficultySelection;
  automaticLevel: ComputerDifficulty;
}

function readComputerPreference(): ComputerPreference {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(COMPUTER_PREFERENCE_STORAGE_KEY) ?? "{}",
    );
    if (typeof parsed !== "object" || parsed === null) {
      return { selection: "beginner", automaticLevel: "beginner" };
    }
    const value = parsed as Record<string, unknown>;
    const selection =
      value.selection === "automatic" ||
      value.selection === "beginner" ||
      value.selection === "intermediate" ||
      value.selection === "advanced"
        ? value.selection
        : "beginner";
    const automaticLevel =
      value.automaticLevel === "beginner" ||
      value.automaticLevel === "intermediate" ||
      value.automaticLevel === "advanced"
        ? value.automaticLevel
        : "beginner";
    return { selection, automaticLevel };
  } catch {
    return { selection: "beginner", automaticLevel: "beginner" };
  }
}

function writeComputerPreference(preference: ComputerPreference): void {
  try {
    window.localStorage.setItem(
      COMPUTER_PREFERENCE_STORAGE_KEY,
      JSON.stringify(preference),
    );
  } catch {
    // A restricted storage context should not prevent the game from loading.
  }
}

function rememberComputerState(computer: GameState["computer"]): void {
  if (!computer) return;
  const previous = readComputerPreference();
  writeComputerPreference({
    selection: computer.selectedDifficulty,
    automaticLevel:
      computer.selectedDifficulty === "automatic"
        ? computer.effectiveDifficulty
        : previous.automaticLevel,
  });
}

function readRulesHidden(mode: GameMode): boolean {
  try {
    const value: unknown = JSON.parse(
      window.localStorage.getItem(RULES_PREFERENCE_STORAGE_KEY) ?? "{}",
    );
    return (
      typeof value === "object" &&
      value !== null &&
      (value as Record<string, unknown>)[mode] === true
    );
  } catch {
    return false;
  }
}

function writeRulesHidden(mode: GameMode, hidden: boolean): void {
  try {
    const current: unknown = JSON.parse(
      window.localStorage.getItem(RULES_PREFERENCE_STORAGE_KEY) ?? "{}",
    );
    const preferences: Record<string, boolean> =
      typeof current === "object" && current !== null
        ? Object.fromEntries(
            Object.entries(current).filter((entry): entry is [string, boolean] =>
              typeof entry[1] === "boolean",
            ),
          )
        : {};
    preferences[mode] = hidden;
    window.localStorage.setItem(
      RULES_PREFERENCE_STORAGE_KEY,
      JSON.stringify(preferences),
    );
  } catch {
    // A restricted storage context should not prevent the game from loading.
  }
}

function rulesSeenForGame(gameId: string): boolean {
  try {
    return window.sessionStorage.getItem(`${RULES_SESSION_PREFIX}${gameId}`) === "seen";
  } catch {
    return false;
  }
}

function markRulesSeenForGame(gameId: string): void {
  try {
    window.sessionStorage.setItem(`${RULES_SESSION_PREFIX}${gameId}`, "seen");
  } catch {
    // A restricted storage context should not prevent the game from loading.
  }
}

class GameSounds {
  private context: AudioContext | null = null;
  muted = false;

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  play(type: "fire" | "impact" | "start"): void {
    if (this.muted) return;
    this.context ??= new AudioContext();
    const context = this.context;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type === "impact" ? "sawtooth" : "square";
    oscillator.frequency.setValueAtTime(
      type === "fire" ? 150 : type === "impact" ? 72 : 420,
      context.currentTime,
    );
    oscillator.frequency.exponentialRampToValueAtTime(
      type === "impact" ? 38 : 90,
      context.currentTime + 0.12,
    );
    gain.gain.setValueAtTime(type === "impact" ? 0.09 : 0.045, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      context.currentTime + 0.14,
    );
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.15);
  }
}

function Brand(): preact.JSX.Element {
  return (
    <a class="brand" href="/" aria-label="Tanks home">
      <span class="brand-mark" aria-hidden="true">
        <span />
      </span>
      <span>TANKS</span>
    </a>
  );
}

interface RulesDialogProps {
  mode: GameMode;
  singlePlayerMode: SinglePlayerMode | null;
  computer: GameState["computer"];
  onClose: (hideNextTime: boolean) => void;
}

function RulesDialog({ mode, singlePlayerMode, computer, onClose }: RulesDialogProps): preact.JSX.Element {
  const [hideNextTime, setHideNextTime] = useState(false);
  const captureTheFlag =
    mode === "capture-the-flag" ||
    (mode === "single-player" && singlePlayerMode === "capture-the-flag");
  const singlePlayer = mode === "single-player";

  return (
    <div class="rules-backdrop" role="presentation">
      <section
        class="rules-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rules-title"
      >
        <p class="eyebrow">{gameModeLabel(mode)}</p>
        <h2 id="rules-title">BATTLE RULES</h2>
        <div class="rules-copy">
          {captureTheFlag ? (
            <>
              <p>Capture the opponent&apos;s flag and bring it back to your starting point to win.</p>
              <ul>
                <li>Touch the opponent&apos;s flag to carry it.</li>
                <li>If the carrier is hit, the flag drops just away from them, opposite their home base, and the tank stays where it was hit.</li>
                <li>Touch your own dropped flag to return it to base.</li>
                <li>There is no life limit. Every kill only creates another chance to recover the flag.</li>
                <li>A carrier hit is immobilized and shielded for 3 seconds; other hit tanks are shielded but can move.</li>
                {singlePlayer && <li>Computer level: {difficultyLabel(computer?.effectiveDifficulty ?? "beginner")}.</li>}
              </ul>
            </>
          ) : singlePlayer ? (
            <>
              <p>Battle the computer and reduce its tank to zero lives before it does the same to you.</p>
              <ul>
                <li>Each tank has three lives.</li>
                <li>A hit shields the tank for 3 seconds without stopping its movement.</li>
                <li>Computer level: {difficultyLabel(computer?.effectiveDifficulty ?? "beginner")}.</li>
                <li>Automatic mode adapts one level at a time to your performance.</li>
                <li>Your selected level and Automatic&apos;s current level are remembered for future games.</li>
              </ul>
            </>
          ) : (
            <>
              <p>Reduce the opponent&apos;s tank to zero lives before they do the same to you.</p>
              <ul>
                <li>Each tank has three lives.</li>
                <li>A damaging hit removes one life and leaves the tank where it was hit.</li>
                <li>A hit shields the tank for 3 seconds without stopping its movement.</li>
              </ul>
            </>
          )}
          <p class="rules-shared">Move with WASD or the arrow keys. Fire with Space.</p>
        </div>
        <label class="rules-preference">
          <input
            type="checkbox"
            checked={hideNextTime}
            onChange={(event) => setHideNextTime(event.currentTarget.checked)}
          />
          <span>Don&apos;t show these rules next time</span>
        </label>
        <button
          class="button button-primary"
          onClick={() => onClose(hideNextTime)}
        >
          CONTINUE
        </button>
      </section>
    </div>
  );
}

function Landing(): preact.JSX.Element {
  const [creating, setCreating] = useState(false);
  const [joinToken, setJoinToken] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState<CreateGameMode>("classic");
  const [singlePlayer, setSinglePlayer] = useState(false);
  const [computerPreference, setComputerPreference] =
    useState<ComputerPreference>(readComputerPreference);

  const selectDifficulty = (selection: DifficultySelection): void => {
    const next = { ...computerPreference, selection };
    setComputerPreference(next);
    writeComputerPreference(next);
  };

  const create = async (): Promise<void> => {
    setCreating(true);
    setError("");
    try {
      const response = await fetch("/api/games", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: singlePlayer ? "single-player" : mode,
          ...(singlePlayer
            ? {
                difficulty: computerPreference.selection,
                automaticLevel: computerPreference.automaticLevel,
                singlePlayerMode:
                  mode === "capture-the-flag"
                    ? "capture-the-flag"
                    : "last-tank-standing",
              }
            : {}),
        }),
      });
      const body = (await response.json()) as {
        gamePath?: string;
        error?: string;
      };
      if (!response.ok || !body.gamePath) {
        throw new Error(body.error ?? "Unable to create a game.");
      }
      window.location.assign(body.gamePath);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to create a game.");
      setCreating(false);
    }
  };

  const join = (event: Event): void => {
    event.preventDefault();
    const token = joinToken.trim().toLowerCase();
    if (!TOKEN_PATTERN.test(token)) {
      setError("Enter the 32-character invitation token.");
      return;
    }
    window.location.assign(`/join/${token}`);
  };

  return (
    <main class="landing">
      <nav class="landing-nav">
        <Brand />
        <span class="live-label"><i /> REAL-TIME BATTLE</span>
      </nav>
      <section class="hero">
        <div class="hero-copy">
          <p class="eyebrow">TWO PLAYERS · ONE BATTLEFIELD</p>
          <h1>
            OUTMANEUVER.
            <br />
            <em>OUTLAST.</em>
          </h1>
          <p class="hero-lead">
            Generate a battlefield, invite your opponent, and fight for the last
            life. Every arena is new. Every shot matters.
          </p>
          <fieldset class="mode-picker">
            <legend>CHOOSE GAME MODE</legend>
            <label class={mode === "classic" ? "mode-option selected" : "mode-option"}>
              <input
                type="radio"
                name="game-mode"
                value="classic"
                checked={mode === "classic"}
                onChange={() => setMode("classic")}
              />
              <span>
                <strong>LAST TANK STANDING</strong>
                <small>Win by eliminating all three lives.</small>
              </span>
            </label>
            <label class={mode === "capture-the-flag" ? "mode-option selected" : "mode-option"}>
              <input
                type="radio"
                name="game-mode"
                value="capture-the-flag"
                checked={mode === "capture-the-flag"}
                onChange={() => setMode("capture-the-flag")}
              />
              <span>
                <strong>CAPTURE THE FLAG</strong>
                <small>Steal the flag and return it to your base.</small>
              </span>
            </label>
          </fieldset>
          <label class={singlePlayer ? "single-player-toggle selected" : "single-player-toggle"}>
            <input
              type="checkbox"
              checked={singlePlayer}
              onChange={(event) => setSinglePlayer(event.currentTarget.checked)}
            />
            <span>
              <strong>SINGLE PLAYER</strong>
              <small>Battle the computer at your chosen level.</small>
            </span>
          </label>
          {singlePlayer && (
            <fieldset class="difficulty-picker">
              <legend>COMPUTER LEVEL</legend>
              {(["beginner", "intermediate", "advanced", "automatic"] as const).map(
                (selection) => (
                  <label
                    class={computerPreference.selection === selection ? "difficulty-option selected" : "difficulty-option"}
                    key={selection}
                  >
                    <input
                      type="radio"
                      name="computer-level"
                      value={selection}
                      checked={computerPreference.selection === selection}
                      onChange={() => selectDifficulty(selection)}
                    />
                    <span>
                      <strong>{difficultyLabel(selection)}</strong>
                    </span>
                  </label>
                ),
              )}
            </fieldset>
          )}
          <div class="hero-actions">
            <button class="button button-primary" onClick={() => void create()} disabled={creating}>
              {creating ? "CREATING BATTLEFIELD…" : "START A NEW GAME"}
              <span aria-hidden="true">→</span>
            </button>
            {!singlePlayer && (
              <form class="join-form" onSubmit={join}>
                <label for="join-token">Have an invitation?</label>
                <div>
                  <input
                    id="join-token"
                    value={joinToken}
                    onInput={(event) =>
                      setJoinToken(event.currentTarget.value.replace(/\s/g, ""))
                    }
                    placeholder="PASTE TOKEN"
                    autocomplete="off"
                    spellcheck={false}
                  />
                  <button type="submit" aria-label="Join game">JOIN</button>
                </div>
              </form>
            )}
          </div>
          {error && <p class="form-error" role="alert">{error}</p>}
        </div>
        <div class="hero-art" aria-hidden="true">
          <div class="radar-ring ring-one" />
          <div class="radar-ring ring-two" />
          <div class="radar-sweep" />
          <div class="hero-tank hero-tank-one"><span /></div>
          <div class="hero-tank hero-tank-two"><span /></div>
          <div class="shell-trail"><i /></div>
          <div class="block block-one" />
          <div class="block block-two" />
          <div class="block block-three" />
        </div>
      </section>
      <footer class="landing-footer">
        <span>DESKTOP + MOBILE</span>
        <span>POWERED BY CLOUDFLARE</span>
      </footer>
    </main>
  );
}

function JoinGame({ token }: { token: string }): preact.JSX.Element {
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");

  const join = async (): Promise<void> => {
    setJoining(true);
    setError("");
    try {
      const response = await fetch(`/api/games/${token}/join`, {
        method: "POST",
      });
      const body = (await response.json()) as {
        gamePath?: string;
        error?: string;
      };
      if (!response.ok || !body.gamePath) {
        throw new Error(body.error ?? "Unable to join this game.");
      }
      window.location.assign(body.gamePath);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to join this game.");
      setJoining(false);
    }
  };

  return (
    <main class="join-page">
      <nav class="landing-nav"><Brand /></nav>
      <section class="join-card">
        <div class="invite-emblem" aria-hidden="true"><span /></div>
        <p class="eyebrow">YOU HAVE BEEN CHALLENGED</p>
        <h1>ENTER THE<br /><em>BATTLEFIELD</em></h1>
        <p>
          Your opponent is waiting. Claim the second tank and prepare for a
          three-life duel.
        </p>
        <button class="button button-primary" onClick={() => void join()} disabled={joining}>
          {joining ? "JOINING…" : "JOIN GAME"} <span>→</span>
        </button>
        {error && <p class="form-error" role="alert">{error}</p>}
        <a href="/" class="text-link">Return home</a>
      </section>
    </main>
  );
}

function LifePips({ lives }: { lives: number }): preact.JSX.Element {
  return (
    <span class="life-pips" aria-label={`${lives} lives remaining`}>
      {[0, 1, 2].map((index) => (
        <i class={index < lives ? "alive" : "lost"} key={index} />
      ))}
    </span>
  );
}

interface ControlsProps {
  setJoystickMovement: (movement: number, axisX: number, axisY: number) => void;
  fire: () => void;
  resetVersion: number;
  swapped: boolean;
}

function Controls({
  setJoystickMovement,
  fire,
  resetVersion,
  swapped,
}: ControlsProps): preact.JSX.Element {
  const joystickRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLSpanElement>(null);
  const activePointerRef = useRef<number | null>(null);

  const resetActiveJoystick = useCallback((): void => {
    activePointerRef.current = null;
    if (knobRef.current) {
      knobRef.current.style.transform = "translate(-50%, -50%)";
    }
    setJoystickMovement(0, 0, 0);
  }, [setJoystickMovement]);

  const resetJoystick = useCallback((pointerId: number): void => {
    if (activePointerRef.current !== pointerId) return;
    resetActiveJoystick();
  }, [resetActiveJoystick]);

  const updateJoystick = useCallback((event: PointerEvent): void => {
    if (activePointerRef.current !== event.pointerId || !joystickRef.current) return;
    const bounds = joystickRef.current.getBoundingClientRect();
    const centerX = bounds.left + bounds.width / 2;
    const centerY = bounds.top + bounds.height / 2;
    const maxDistance = Math.max(1, Math.min(bounds.width, bounds.height) / 2 - 24);
    const dx = event.clientX - centerX;
    const dy = event.clientY - centerY;
    const distance = Math.hypot(dx, dy);
    const scale = distance > maxDistance ? maxDistance / distance : 1;
    const knobX = dx * scale;
    const knobY = dy * scale;
    if (knobRef.current) {
      knobRef.current.style.transform = `translate(calc(-50% + ${knobX}px), calc(-50% + ${knobY}px))`;
    }

    const rawAxisX = knobX / maxDistance;
    const rawAxisY = knobY / maxDistance;
    const rawMagnitude = Math.hypot(rawAxisX, rawAxisY);
    const deadzone = 0.12;
    const adjustedMagnitude = rawMagnitude <= deadzone
      ? 0
      : Math.min(1, (rawMagnitude - deadzone) / (1 - deadzone));
    const axisScale = rawMagnitude > 0 ? adjustedMagnitude / rawMagnitude : 0;
    const axisX = rawAxisX * axisScale;
    const axisY = rawAxisY * axisScale;
    let movement = 0;
    if (axisY < -0.03) movement |= INPUT_BITS.forward;
    if (axisY > 0.03) movement |= INPUT_BITS.backward;
    if (axisX < -0.03) movement |= INPUT_BITS.left;
    if (axisX > 0.03) movement |= INPUT_BITS.right;
    setJoystickMovement(movement, axisX, axisY);
  }, [setJoystickMovement]);

  const startJoystick = (event: PointerEvent): void => {
    event.preventDefault();
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (activePointerRef.current !== null) return;
    activePointerRef.current = event.pointerId;
    updateJoystick(event);
  };

  useEffect(() => {
    const moveFromWindow = (event: PointerEvent): void => {
      updateJoystick(event);
    };
    const releaseFromWindow = (event: PointerEvent): void => {
      resetJoystick(event.pointerId);
    };
    const releaseAll = (): void => {
      resetActiveJoystick();
    };
    window.addEventListener("pointermove", moveFromWindow);
    window.addEventListener("pointerup", releaseFromWindow);
    window.addEventListener("pointercancel", releaseFromWindow);
    window.addEventListener("blur", releaseAll);
    document.addEventListener("visibilitychange", releaseAll);
    return () => {
      window.removeEventListener("pointermove", moveFromWindow);
      window.removeEventListener("pointerup", releaseFromWindow);
      window.removeEventListener("pointercancel", releaseFromWindow);
      window.removeEventListener("blur", releaseAll);
      document.removeEventListener("visibilitychange", releaseAll);
      resetActiveJoystick();
    };
  }, [resetActiveJoystick, resetJoystick, updateJoystick]);

  useEffect(() => {
    resetActiveJoystick();
  }, [resetActiveJoystick, resetVersion]);

  return (
    <div
      class={`controls ${swapped ? "controls-swapped" : ""}`}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div
        ref={joystickRef}
        class="joystick"
        role="group"
        aria-label="Virtual movement joystick"
        onPointerDown={startJoystick}
      >
        <span class="joystick-crosshair" aria-hidden="true" />
        <span ref={knobRef} class="joystick-knob" aria-hidden="true" />
      </div>
      <button
        class="fire-button"
        aria-label="Fire projectile"
        onPointerDown={(event) => {
          event.preventDefault();
          fire();
        }}
      >
        <span>FIRE</span>
        <i aria-hidden="true" />
      </button>
    </div>
  );
}

function GameScreen({ token }: { token: string }): preact.JSX.Element {
  const [state, setState] = useState<GameState | null>(null);
  const [playerId, setPlayerId] = useState<PlayerId | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [latency, setLatency] = useState<number | null>(null);
  const [muted, setMuted] = useState(false);
  const [controlsSwapped, setControlsSwapped] = useState(readControlsSwapped);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const [controlsResetVersion, setControlsResetVersion] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<BattlefieldRenderer | null>(null);
  const socketRef = useRef<GameSocket | null>(null);
  const stateRef = useRef<GameState | null>(null);
  const movementRef = useRef(0);
  const inputAxesRef = useRef({ axisX: 0, axisY: 0 });
  const joystickMovementRef = useRef(0);
  const joystickAxesRef = useRef({ axisX: 0, axisY: 0 });
  const keyboardRef = useRef(new Set<string>());
  const soundsRef = useRef(new GameSounds());
  const rulesModeRef = useRef<GameMode | null>(null);

  const applyMovement = useCallback(() => {
    let pointerMask = 0;
    pointerMask |= joystickMovementRef.current;
    const next = movementMask(keyboardRef.current) | pointerMask;
    const keyboardAxes = movementAxes(movementMask(keyboardRef.current));
    const axisX = Math.max(-1, Math.min(1, keyboardAxes.axisX + joystickAxesRef.current.axisX));
    const axisY = Math.max(-1, Math.min(1, keyboardAxes.axisY + joystickAxesRef.current.axisY));
    if (
      next === movementRef.current &&
      axisX === inputAxesRef.current.axisX &&
      axisY === inputAxesRef.current.axisY
    ) return;
    movementRef.current = next;
    inputAxesRef.current = { axisX, axisY };
    rendererRef.current?.setMovement(next, axisX, axisY);
    socketRef.current?.sendInput(next, false, axisX, axisY);
  }, []);

  const resetNavigation = useCallback((): void => {
    keyboardRef.current.clear();
    joystickMovementRef.current = 0;
    joystickAxesRef.current = { axisX: 0, axisY: 0 };
    movementRef.current = 0;
    inputAxesRef.current = { axisX: 0, axisY: 0 };
    rendererRef.current?.setMovement(0, 0, 0);
    socketRef.current?.sendInput(0, false, 0, 0);
    setControlsResetVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        CONTROL_LAYOUT_STORAGE_KEY,
        controlsSwapped ? "swapped" : "default",
      );
    } catch {
      // A restricted storage context should not prevent the game from loading.
    }
  }, [controlsSwapped]);

  const swapControls = (): void => {
    resetNavigation();
    setControlsSwapped((swapped) => !swapped);
  };

  useEffect(() => {
    if (!state || rulesModeRef.current === state.mode) return;
    rulesModeRef.current = state.mode;
    setRulesOpen(!readRulesHidden(state.mode) && !rulesSeenForGame(state.gameId));
  }, [state]);

  const closeRules = (hideNextTime: boolean): void => {
    if (state) {
      writeRulesHidden(state.mode, hideNextTime);
      markRulesSeenForGame(state.gameId);
    }
    setRulesOpen(false);
  };

  useEffect(() => {
    if (!canvasRef.current) return;
    const renderer = new BattlefieldRenderer(canvasRef.current);
    rendererRef.current = renderer;
    return () => {
      renderer.destroy();
      rendererRef.current = null;
    };
  }, []);

  useEffect(() => {
    soundsRef.current.setMuted(muted);
  }, [muted]);

  useEffect(() => {
    const handleMessage = (message: ServerMessage): void => {
      const applyServerState = (nextState: GameState): void => {
        if (
          stateRef.current?.phase === "playing" &&
          nextState.phase !== "playing"
        ) {
          resetNavigation();
        }
        stateRef.current = nextState;
        setState(nextState);
        rememberComputerState(nextState.computer);
        rendererRef.current?.setState(nextState);
      };
      if (message.type === "welcome") {
        setPlayerId(message.playerId);
        rendererRef.current?.setPlayer(message.playerId);
        applyServerState(message.state);
      } else if (message.type === "snapshot") {
        applyServerState(message.state);
      } else if (message.type === "pong") {
        setLatency(Math.max(0, Date.now() - message.sentAt));
      } else if (message.type === "event") {
        if (message.event.type === "shot") soundsRef.current.play("fire");
        if (message.event.type === "impact") {
          soundsRef.current.play("impact");
          setNotice(`PLAYER ${message.event.playerId} HIT`);
          window.setTimeout(() => setNotice(""), 900);
        }
        if (message.event.type === "flag-picked-up") {
          setNotice(`PLAYER ${message.event.playerId} HAS THE FLAG`);
          window.setTimeout(() => setNotice(""), 1_200);
        }
        if (message.event.type === "flag-dropped") {
          setNotice(`PLAYER ${message.event.playerId} DROPPED THE FLAG`);
          window.setTimeout(() => setNotice(""), 1_200);
        }
        if (message.event.type === "flag-returned") {
          setNotice(`PLAYER ${message.event.playerId} RETURNED THE FLAG`);
          window.setTimeout(() => setNotice(""), 1_200);
        }
        if (message.event.type === "round-started") soundsRef.current.play("start");
      } else if (message.type === "error") {
        setNotice(message.message);
      }
    };
    const socket = new GameSocket({
      token,
      onMessage: handleMessage,
      onStatus: (nextStatus) => {
        setStatus(nextStatus);
        if (nextStatus === "reconnecting" || nextStatus === "unavailable") {
          resetNavigation();
        }
      },
    });
    socketRef.current = socket;
    socket.connect();
    const repeat = window.setInterval(() => {
      if (movementRef.current !== 0) {
        socket.sendInput(movementRef.current, false);
      }
    }, 100);
    return () => {
      window.clearInterval(repeat);
      socket.close();
      socketRef.current = null;
    };
  }, [resetNavigation, token]);

  useEffect(() => {
    const gameKeys = new Set([
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "KeyW",
      "KeyA",
      "KeyS",
      "KeyD",
      "Space",
    ]);
    const down = (event: KeyboardEvent): void => {
      if (!gameKeys.has(event.code)) return;
      event.preventDefault();
      if (event.code === "Space") {
        if (!event.repeat) {
          socketRef.current?.sendInput(movementRef.current, true);
          soundsRef.current.play("fire");
        }
        return;
      }
      keyboardRef.current.add(event.code);
      applyMovement();
    };
    const up = (event: KeyboardEvent): void => {
      if (!gameKeys.has(event.code)) return;
      event.preventDefault();
      keyboardRef.current.delete(event.code);
      applyMovement();
    };
    const release = (): void => {
      keyboardRef.current.clear();
      joystickMovementRef.current = 0;
      joystickAxesRef.current = { axisX: 0, axisY: 0 };
      applyMovement();
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", release);
    window.addEventListener("pagehide", release);
    document.addEventListener("visibilitychange", release);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", release);
      window.removeEventListener("pagehide", release);
      document.removeEventListener("visibilitychange", release);
    };
  }, [applyMovement]);

  const setJoystickMovement = useCallback((movement: number, axisX: number, axisY: number): void => {
    joystickMovementRef.current = movement;
    joystickAxesRef.current = { axisX, axisY };
    applyMovement();
  }, [applyMovement]);
  const fire = (): void => {
    socketRef.current?.sendInput(movementRef.current, true);
    soundsRef.current.play("fire");
  };
  const inviteUrl = useMemo(
    () => `${window.location.origin}/join/${token}`,
    [token],
  );
  const share = async (): Promise<void> => {
    if (navigator.share) {
      await navigator.share({
        title: "Join my Tanks game",
        text: "You have been challenged to a two-player tank battle.",
        url: inviteUrl,
      });
      return;
    }
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  };

  const opponentId = playerId === 1 ? 2 : 1;
  const countdown =
    state?.phase === "countdown" && state.countdownEndsAt
      ? Math.max(1, Math.ceil((state.countdownEndsAt - state.serverTime) / 1_000))
      : null;
  const local = playerId && state ? state.tanks[playerId] : null;
  const opponent = state ? state.tanks[opponentId] : null;
  const ready = playerId && state ? state.rematchReady[playerId] : false;
  const computerLevel = state?.computer
    ? `${state.computer.selectedDifficulty === "automatic" ? "AUTO · " : ""}${difficultyLabel(state.computer.effectiveDifficulty)}`
    : null;

  return (
    <main class="game-page">
      <header class="game-header">
        <Brand />
        <div class="round-label">
          <span>ROUND</span>
          <b>{String(state?.round ?? 1).padStart(2, "0")}</b>
          <small>{gameModeLabel(state?.mode ?? "classic", state?.singlePlayerMode ?? null)}</small>
        </div>
        <div class={`connection connection-${status}`}>
          <i />
          {status === "connected"
            ? latency !== null && latency > 180
              ? `${latency} MS`
              : "LIVE"
            : status.replace("-", " ").toUpperCase()}
        </div>
        <button
          class="icon-button"
          onClick={() => setMuted((value) => !value)}
          aria-label={muted ? "Unmute sounds" : "Mute sounds"}
        >
          {muted ? "MUTED" : "SOUND"}
        </button>
        <button
          class="icon-button control-swap-button"
          onClick={swapControls}
          aria-label={controlsSwapped ? "Use default control sides" : "Swap control sides"}
          title="Swap joystick and fire button sides"
        >
          SWAP
        </button>
        <button
          class="icon-button rules-button"
          onClick={() => setRulesOpen(true)}
          aria-label="Review game rules"
        >
          RULES
        </button>
      </header>

      <section class="hud" aria-label="Match status">
        <div class="player-card player-one">
          <span class="player-dot" />
          <div><small>{playerId === 1 ? "YOU" : "PLAYER 1"}</small><b>AMBER</b></div>
          {usesCaptureTheFlag(state)
            ? <span class="no-lives">NO LIMIT</span>
            : <LifePips lives={state?.tanks[1].lives ?? 3} />}
        </div>
        <div class="versus">VS</div>
        <div class="player-card player-two">
          {usesCaptureTheFlag(state)
            ? <span class="no-lives">NO LIMIT</span>
            : <LifePips lives={state?.tanks[2].lives ?? 3} />}
          <div><small>{state?.mode === "single-player" ? `COMPUTER · ${computerLevel ?? "BEGINNER"}` : playerId === 2 ? "YOU" : "PLAYER 2"}</small><b>TEAL</b></div>
          <span class="player-dot" />
        </div>
      </section>

      <section class="combat-row">
        <section class="battlefield-wrap">
          <canvas ref={canvasRef} class="battlefield" aria-label="Tanks battlefield" />
          {notice && <div class="game-notice">{notice}</div>}
          {state?.phase === "waiting" && (
            <div class="game-overlay waiting-overlay">
              <p class="eyebrow">BATTLEFIELD READY</p>
              {state.mode === "single-player" ? (
                <>
                  <h2>COMPUTER<br />OPPONENT READY</h2>
                  <p>The battle begins as soon as your connection is ready.</p>
                  <p class="difficulty-status">LEVEL {difficultyLabel(state.computer?.effectiveDifficulty ?? "beginner")}</p>
                </>
              ) : (
                <>
                  <h2>INVITE YOUR<br />OPPONENT</h2>
                  <p>Share this private link. The battle begins when they connect.</p>
                  <div class="invite-box"><code>{inviteUrl}</code></div>
                  <button class="button button-primary" onClick={() => void share()}>
                    {copied ? "LINK COPIED" : "SHARE INVITATION"}
                  </button>
                </>
              )}
            </div>
          )}
          {state?.phase === "paused" && (
            <div class="game-overlay compact-overlay">
              <span class="pause-icon">Ⅱ</span>
              <h2>GAME PAUSED</h2>
              <p>{state.mode === "single-player" ? "Waiting for you to reconnect." : "Waiting for both players to reconnect."}</p>
            </div>
          )}
          {countdown !== null && (
            <div class="countdown" key={countdown}>
              <small>GET READY</small>
              <b>{countdown}</b>
            </div>
          )}
          {state?.phase === "gameover" && (
            <div class="game-overlay gameover-overlay">
              <p class="eyebrow">BATTLE COMPLETE</p>
              <h2>
                {state.winner === playerId ? "VICTORY" : "DEFEAT"}
              </h2>
              <p>
                {state.winReason === "capture"
                  ? state.mode === "single-player"
                    ? state.winner === 1
                      ? "You captured the computer's flag."
                      : "The computer captured your flag."
                    : `Player ${state.winner} captured the flag.`
                  : state.mode === "single-player"
                    ? state.winner === 1
                      ? "You defeated the computer."
                      : "The computer controls the battlefield."
                    : `Player ${state.winner} controls the battlefield.`} Ready for a
                new arena?
              </p>
              <button
                class={`button ${ready ? "button-ready" : "button-primary"}`}
                onClick={() => socketRef.current?.sendRematch(!ready)}
              >
                {ready
                  ? state.mode === "single-player"
                    ? "READY — STARTING"
                    : "READY — WAITING FOR OPPONENT"
                  : "PLAY AGAIN"}
              </button>
              {state.mode !== "single-player" && (
                <small>
                  Opponent: {state.rematchReady[opponentId] ? "ready" : "deciding"}
                </small>
              )}
            </div>
          )}
          {status === "unavailable" && !state && (
            <div class="game-overlay compact-overlay">
              <h2>SESSION UNAVAILABLE</h2>
              <p>This browser is not registered for the game, or the invitation expired.</p>
              <a class="button button-secondary" href={`/join/${token}`}>OPEN INVITATION</a>
            </div>
          )}
        </section>

        {state?.phase === "playing" && (
          <Controls
            setJoystickMovement={setJoystickMovement}
            fire={fire}
            resetVersion={controlsResetVersion}
            swapped={controlsSwapped}
          />
        )}
      </section>

      {state && rulesOpen && (
        <RulesDialog
          mode={state.mode}
          singlePlayerMode={state.singlePlayerMode}
          computer={state.computer}
          onClose={closeRules}
        />
      )}

      <div class="desktop-legend" aria-hidden="true">
        <span>
          <kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd>
          <i>/</i>
          <kbd>↑</kbd><kbd>←</kbd><kbd>↓</kbd><kbd>→</kbd> MOVE
        </span>
        <span><kbd>SPACE</kbd> FIRE</span>
        {local && local.invulnerableUntil > Date.now() && <b>SHIELD ACTIVE</b>}
        {opponent && opponent.invulnerableUntil > Date.now() && <b>OPPONENT SHIELDED</b>}
      </div>
    </main>
  );
}

function NotFound(): preact.JSX.Element {
  return (
    <main class="join-page">
      <section class="join-card">
        <Brand />
        <h1>LOST IN<br /><em>THE FIELD</em></h1>
        <p>That battlefield route does not exist.</p>
        <a class="button button-primary" href="/">RETURN HOME</a>
      </section>
    </main>
  );
}

function App(): preact.JSX.Element {
  const path = window.location.pathname;
  const join = path.match(/^\/join\/([a-f0-9]{32})\/?$/);
  const game = path.match(/^\/game\/([a-f0-9]{32})\/?$/);
  if (join?.[1]) return <JoinGame token={join[1]} />;
  if (game?.[1]) return <GameScreen token={game[1]} />;
  if (path === "/" || path === "") return <Landing />;
  return <NotFound />;
}

render(<App />, document.getElementById("app")!);
